import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllCompanies, getCompanyUserCount } from '../../lib/supabase/companies';
import {
    FiGrid, FiPlus, FiSearch, FiHome, FiUsers, FiDollarSign,
    FiClock, FiCheckCircle, FiAlertCircle, FiTrendingUp, FiFilter,
    FiCalendar, FiMapPin, FiMail, FiDatabase, FiActivity
} from 'react-icons/fi';

const Companies = () => {
    const navigate = useNavigate();
    const { companySlug } = useParams();
    const { userProfile } = useAuth();
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPlan, setFilterPlan] = useState('all');
    const [userCounts, setUserCounts] = useState({});

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        filterCompanies();
    }, [searchTerm, filterStatus, filterPlan, companies]);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const data = await getAllCompanies();
            setCompanies(data);
            setFilteredCompanies(data);

            // Fetch user counts for all companies
            const counts = {};
            await Promise.all(
                data.map(async (company) => {
                    const count = await getCompanyUserCount(company.id);
                    counts[company.id] = count;
                })
            );
            setUserCounts(counts);
        } catch (error) {
            console.error('Error fetching companies:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterCompanies = () => {
        let filtered = companies;

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(company =>
                company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                company.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
                company.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Status filter
        if (filterStatus !== 'all') {
            filtered = filtered.filter(company => {
                if (filterStatus === 'active') return company.is_active;
                if (filterStatus === 'inactive') return !company.is_active;
                return true;
            });
        }

        // Plan filter
        if (filterPlan !== 'all') {
            filtered = filtered.filter(company => company.subscription_plan === filterPlan);
        }

        setFilteredCompanies(filtered);
    };

    const handleCompanyClick = (company) => {
        navigate(`/${companySlug}/companies/${company.slug}`);
    };

    const handleCreateCompany = () => {
        navigate(`/${companySlug}/companies/create`);
    };

    const getStatusBadge = (company) => {
        if (company.is_active) {
            return (
                <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-lg text-xs font-medium border border-green-200 dark:border-green-800">
                    <FiCheckCircle className="w-3 h-3" />
                    Active
                </div>
            );
        }
        return (
            <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium border border-gray-200 dark:border-gray-800">
                <FiAlertCircle className="w-3 h-3" />
                Inactive
            </div>
        );
    };

    const getPlanBadge = (plan) => {
        const planStyles = {
            free: 'bg-gray-50 dark:bg-gray-900/20 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800',
            starter: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
            professional: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
            enterprise: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
        };

        return (
            <span className={`px-2 py-1 rounded-lg text-xs font-medium border ${planStyles[plan] || planStyles.free}`}>
                {plan?.charAt(0).toUpperCase() + plan?.slice(1) || 'Free'}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const getStatsForCompany = (company) => {
        const actualUsers = userCounts[company.id] || 0;
        const maxUsers = company.max_users;

        return [
            {
                icon: FiUsers,
                value: actualUsers,
                label: 'Active Users',
                color: 'text-blue-600 dark:text-blue-400',
                subtitle: maxUsers ? `/ ${maxUsers}` : null
            },
            {
                icon: FiDollarSign,
                value: `${company.currency || 'AUD'} ${company.base_price || '0'}`,
                label: 'Base Price',
                color: 'text-green-600 dark:text-green-400'
            },
            {
                icon: FiDatabase,
                value: company.max_storage_gb ? `${company.max_storage_gb} GB` : '∞',
                label: 'Storage',
                color: 'text-purple-600 dark:text-purple-400'
            }
        ];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                        <FiGrid className="w-8 h-8 text-blue-600" />
                        Companies
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        Manage all client companies and their subscriptions
                    </p>
                </div>
                <button
                    onClick={handleCreateCompany}
                    className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors shadow-lg shadow-blue-500/30"
                >
                    <FiPlus className="w-5 h-5" />
                    Create Company
                </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Total Companies</p>
                            <p className="text-3xl font-bold text-blue-900 dark:text-blue-100 mt-1">{companies.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-600/10 dark:bg-blue-400/10 rounded-xl flex items-center justify-center">
                            <FiHome className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-green-600 dark:text-green-400 text-sm font-medium">Active</p>
                            <p className="text-3xl font-bold text-green-900 dark:text-green-100 mt-1">
                                {companies.filter(c => c.is_active).length}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-green-600/10 dark:bg-green-400/10 rounded-xl flex items-center justify-center">
                            <FiCheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">Enterprise Plans</p>
                            <p className="text-3xl font-bold text-purple-900 dark:text-purple-100 mt-1">
                                {companies.filter(c => c.subscription_plan === 'enterprise').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-purple-600/10 dark:bg-purple-400/10 rounded-xl flex items-center justify-center">
                            <FiTrendingUp className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                    </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-amber-600 dark:text-amber-400 text-sm font-medium">Trial Period</p>
                            <p className="text-3xl font-bold text-amber-900 dark:text-amber-100 mt-1">
                                {companies.filter(c => c.subscription_status === 'trial').length}
                            </p>
                        </div>
                        <div className="w-12 h-12 bg-amber-600/10 dark:bg-amber-400/10 rounded-xl flex items-center justify-center">
                            <FiClock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-zinc-800 rounded-xl p-6 border border-gray-200 dark:border-zinc-700 shadow-sm">
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, slug, or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Status Filter */}
                    <div className="relative">
                        <FiFilter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="pl-11 pr-10 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    {/* Plan Filter */}
                    <div className="relative">
                        <select
                            value={filterPlan}
                            onChange={(e) => setFilterPlan(e.target.value)}
                            className="px-4 py-3 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer pr-10"
                        >
                            <option value="all">All Plans</option>
                            <option value="free">Free</option>
                            <option value="starter">Starter</option>
                            <option value="professional">Professional</option>
                            <option value="enterprise">Enterprise</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Companies Grid */}
            {filteredCompanies.length === 0 ? (
                <div className="text-center py-16">
                    <FiHome className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No companies found</h3>
                    <p className="text-gray-600 dark:text-gray-400">
                        {searchTerm || filterStatus !== 'all' || filterPlan !== 'all'
                            ? 'Try adjusting your filters'
                            : 'Get started by creating your first company'
                        }
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredCompanies.map((company) => (
                        <div
                            key={company.id}
                            onClick={() => handleCompanyClick(company)}
                            className="group bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-xl hover:shadow-xl hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 cursor-pointer flex flex-col h-full"
                        >
                            {/* Card Header with gradient */}
                            <div className="relative">
                                <div
                                    className="h-24 relative overflow-hidden rounded-t-xl"
                                    style={{
                                        background: `linear-gradient(135deg, ${company.primary_color || '#3b82f6'} 0%, ${company.secondary_color || '#8b5cf6'} 100%)`
                                    }}
                                >
                                    <div className="absolute inset-0 bg-black/10"></div>
                                </div>

                                {/* Company Logo - positioned relative to card, not gradient */}
                                <div className="absolute bottom-0 left-6 transform translate-y-1/2">
                                    {company.logo_url ? (
                                        <img
                                            src={company.logo_url}
                                            alt={company.name}
                                            className="w-16 h-16 rounded-xl border-4 border-white dark:border-zinc-800 bg-white object-contain shadow-lg"
                                        />
                                    ) : (
                                        <div className="w-16 h-16 rounded-xl border-4 border-white dark:border-zinc-800 bg-white flex items-center justify-center shadow-lg">
                                            <FiHome className="w-8 h-8 text-gray-400" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Card Content */}
                            <div className="pt-10 px-6 pb-6 flex-1 flex flex-col">
                                {/* Company Name & Status */}
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-1">
                                            {company.name}
                                        </h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">
                                            /{company.slug}
                                        </p>
                                    </div>
                                    {getStatusBadge(company)}
                                </div>

                                {/* Contact Info */}
                                <div className="space-y-2 mb-4">
                                    {company.contact_email && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                            <FiMail className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">{company.contact_email}</span>
                                        </div>
                                    )}
                                    {company.city && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                            <FiMapPin className="w-4 h-4 flex-shrink-0" />
                                            <span>{company.city}, {company.country || 'Australia'}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-3 gap-4 mb-4 py-4 border-t border-b border-gray-100 dark:border-zinc-700">
                                    {getStatsForCompany(company).map((stat, index) => (
                                        <div key={index} className="text-center">
                                            <stat.icon className={`w-5 h-5 mx-auto mb-1 ${stat.color}`} />
                                            <div className="flex items-baseline justify-center gap-1">
                                                <p className="text-sm font-bold text-gray-900 dark:text-white">
                                                    {stat.value}
                                                </p>
                                                {stat.subtitle && (
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        {stat.subtitle}
                                                    </p>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {stat.label}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {/* Additional Info Row */}
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="flex items-center gap-2 text-xs">
                                        <FiActivity className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {company.subscription_status?.charAt(0).toUpperCase() + company.subscription_status?.slice(1)}
                                            </p>
                                            <p className="text-gray-500 dark:text-gray-400">Status</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs">
                                        <FiCalendar className="w-4 h-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">
                                                {formatDate(company.created_at)}
                                            </p>
                                            <p className="text-gray-500 dark:text-gray-400">Created</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Plan Badge */}
                                <div className="flex items-center justify-center mt-auto pt-3 border-t border-gray-100 dark:border-zinc-700">
                                    {getPlanBadge(company.subscription_plan)}
                                </div>
                            </div>

                            {/* Hover Indicator */}
                            <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                        </div>
                    ))}
                </div>
            )}

            {/* Results Count */}
            {filteredCompanies.length > 0 && (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                    Showing {filteredCompanies.length} of {companies.length} companies
                </div>
            )}
        </div>
    );
};

export default Companies;
